import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { beyondCurtain, collections, philosophyPillars } from "@/mocks/collections";
import Reveal from "@/components/base/Reveal";

export default function CollectionsSection() {
  const [active, setActive] = useState(collections[0].id);
  const current = collections.find((c) => c.id === active) ?? collections[0];
  const activeIndex = collections.findIndex((c) => c.id === active);

  // ── Active-tab slider indicator ────────────────────────────
  const tabsContainerRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [indicator, setIndicator] = useState<{ left: number; width: number }>({ left: 0, width: 0 });

  const measureIndicator = () => {
    const wrapper = tabsContainerRef.current;
    const el = tabRefs.current[active];
    if (!wrapper || !el) return;
    const wrapperRect = wrapper.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    setIndicator({ left: r.left - wrapperRect.left, width: r.width });
  };

  useLayoutEffect(() => {
    measureIndicator();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  useEffect(() => {
    const onResize = () => measureIndicator();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Mouse-tracking parallax on the featured image ────────
  const parallaxRef = useRef<HTMLDivElement | null>(null);
  const handleParallax = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = parallaxRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    el.style.setProperty("--px", String(px));
    el.style.setProperty("--py", String(py));
  };
  const resetParallax = () => {
    const el = parallaxRef.current;
    if (!el) return;
    el.style.setProperty("--px", "0");
    el.style.setProperty("--py", "0");
  };

  // ── Letter-by-letter title animation when switching ──────
  const renderLetters = (text: string) =>
    text.split("").map((ch, i) => (
      <span
        key={`${text}-${i}`}
        className="lum-letter"
        style={{ animationDelay: `${i * 45}ms` }}
      >
        {ch === " " ? "\u00A0" : ch}
      </span>
    ));

  return (
    <section
      id="collections-grid"
      className="relative w-full bg-[var(--lum-bg)] border-t border-[var(--lum-gold)]/12 py-24 md:py-32 px-6 md:px-12 overflow-hidden"
    >
      {/* Decorative rotating ornament */}
      <div className="lum-ornament lum-spin-slow hidden md:block" style={{ width: 520, height: 520, top: -160, right: -180 }} />
      <div className="lum-ornament lum-spin-rev hidden md:block" style={{ width: 320, height: 320, top: 40, right: 60 }} />

      <div className="relative max-w-7xl mx-auto">
        {/* Manifesto strip / Philosophy */}
        <div className="mb-20 md:mb-28">
          <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
            <div className="max-w-2xl">
              <div className="font-label text-[11px] text-[var(--lum-gold)] mb-5">
                この会から / 持ち帰れるもの
              </div>
              <h4 className="font-jp text-4xl md:text-5xl lg:text-[56px] leading-[1.3] text-[var(--lum-cream)] font-medium">
                <a href="#collections-grid" className="cursor-pointer lum-underline">
                  名刺を交換した人の顔を、<br />
                  <span className="text-[var(--lum-gold)]">
                    何人思い出せますか。
                  </span>
                </a>
              </h4>
            </div>
            <p className="font-jp text-sm md:text-[15px] text-[var(--lum-cream)]/65 max-w-md leading-[2.0]">
              僕は、あまり思い出せませんでした。接点は増えていくのに、関係は残らない。そんな交流の形に、少しだけ違和感がありました。だからいま、月に一度、シーシャ屋に集まって、仕事の話も、仕事じゃない話も、気の向くままに話す会をやっています。「社長の放課後部」といいます。
            </p>
          </Reveal>

          {/* Three pillars */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--lum-gold)]/10 border border-[var(--lum-gold)]/10 rounded-lg overflow-hidden">
            {philosophyPillars.map((p, i) => (
              <Reveal
                key={p.french}
                delay={(i + 1) as 1 | 2 | 3}
                className="group bg-[var(--lum-bg)] p-8 md:p-10 flex flex-col gap-5 hover:bg-[#e6f0ff] transition-colors duration-500 relative overflow-hidden"
              >
                {/* hover sheen */}
                <span className="absolute -top-1/2 -left-1/4 w-1/2 h-[200%] bg-gradient-to-b from-transparent via-[var(--lum-gold)]/8 to-transparent rotate-12 opacity-0 group-hover:opacity-100 translate-x-[-30%] group-hover:translate-x-[400%] transition-all duration-[1400ms] ease-out pointer-events-none" />
                <div className="flex items-center justify-between relative">
                  <div className="relative w-11 h-11 flex items-center justify-center rounded-full border border-[var(--lum-gold)]/40 text-[var(--lum-gold)] transition-all duration-500 group-hover:border-[var(--lum-gold)] group-hover:bg-[var(--lum-gold)]/10 group-hover:rotate-12">
                    <i className={`${p.icon} text-lg`} />
                    <span className="lum-ripple" />
                    <span className="lum-ripple" style={{ animationDelay: "0.7s" }} />
                  </div>
                  <span className="font-display italic text-sm text-[var(--lum-cream)]/40">
                    {p.index}
                  </span>
                </div>
                <div className="relative">
                  <div className="font-label text-[10px] text-[var(--lum-gold)]/85 mb-2">
                    / {p.english.toUpperCase()}
                  </div>
                  <h5 className="font-jp text-2xl md:text-3xl text-[var(--lum-cream)] mb-4 lum-sweep-line inline-block font-medium">
                    {p.french}
                  </h5>
                  <p className="font-jp text-sm leading-[2.0] text-[var(--lum-cream)]/65">
                    {p.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Section divider */}
        <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14 md:mb-16">
          <div className="max-w-2xl">
            <div className="font-label text-[11px] text-[var(--lum-gold)] mb-5">
              この会から持ち帰れるもの / 詳しく見る
            </div>
            <h4 className="font-jp text-5xl md:text-6xl lg:text-[64px] leading-[1.2] text-[var(--lum-cream)] font-medium">
              <a href="#collections-grid" className="cursor-pointer lum-underline">
                ３つの持ち帰りもの。
              </a>
            </h4>
          </div>
          <p className="font-jp text-sm md:text-base text-[var(--lum-cream)]/65 max-w-md leading-[2.0]">
            名刺交換より大切なことが、この会にはあります。それを、ひとつひとつ言葉にしてみました。
          </p>
        </Reveal>

        {/* Filter tabs */}
        <Reveal className="mb-10 md:mb-14" delay={1}>
          <div
            ref={tabsContainerRef}
            className="relative flex flex-wrap items-center gap-2 pb-2 border-b border-[var(--lum-gold)]/15"
          >
            {collections.map((c) => (
              <button
                key={c.id}
                ref={(el) => {
                  tabRefs.current[c.id] = el;
                }}
                type="button"
                onClick={() => setActive(c.id)}
                className={`group inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full border font-jp text-[12px] transition-all duration-400 cursor-pointer whitespace-nowrap ${
                  active === c.id
                    ? "border-[var(--lum-gold)] bg-[var(--lum-gold)]/12 text-[var(--lum-gold)] scale-[1.04]"
                    : "border-[var(--lum-cream)]/20 text-[var(--lum-cream)]/65 hover:text-[var(--lum-cream)] hover:border-[var(--lum-cream)]/40 hover:scale-[1.02]"
                }`}
              >
                <span>{c.name}</span>
                {c.french && (
                  <span className="text-[10px] opacity-60">/ {c.french}</span>
                )}
              </button>
            ))}
            <span
              className="lum-tab-indicator"
              style={{ left: indicator.left, width: indicator.width }}
            />
          </div>
        </Reveal>

        {/* Active collection feature panel */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-stretch">
          {/* Image with mouse-tracking parallax */}
          <div className="lg:col-span-7 relative">
            <div
              ref={parallaxRef}
              onMouseMove={handleParallax}
              onMouseLeave={resetParallax}
              className="lum-parallax relative overflow-hidden rounded-lg bg-[#e6f0ff]"
            >
              <div className="lum-parallax-inner w-full aspect-[4/5] lg:aspect-[5/6] overflow-hidden" key={current.id}>
                <div
                  className="w-full h-full bg-cover bg-top lum-fade-img lum-kenburns"
                  style={{ backgroundImage: `url(${current.image})` }}
                />
              </div>

              <span className="lum-seam" />

              <div className="absolute top-6 left-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--lum-bg)]/80 backdrop-blur-md font-jp text-[11px] text-[var(--lum-cream)]/90">
                <span className="w-1.5 h-1.5 flex items-center justify-center rounded-full bg-[var(--lum-gold)] animate-pulse" />
                {current.priceFrom}
              </div>

              <div className="absolute top-6 right-6 flex items-end gap-1.5 font-display text-[var(--lum-cream)]/85">
                <span
                  key={`num-${current.id}`}
                  className="lum-flip text-3xl text-[var(--lum-gold)] leading-none tabular-nums"
                >
                  {String(activeIndex + 1).padStart(2, "0")}
                </span>
                <span className="text-[11px] mb-1 font-label text-[var(--lum-cream)]/40">
                  / {String(collections.length).padStart(2, "0")}
                </span>
              </div>

              <div className="absolute bottom-6 left-6 right-6 flex items-center justify-between gap-4 px-4 py-3 rounded-md bg-[var(--lum-bg)]/80 backdrop-blur-md border border-[var(--lum-gold)]/15">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-7 h-7 flex items-center justify-center rounded-full border border-[var(--lum-gold)]/40 text-[var(--lum-gold)] shrink-0">
                    <i className="ri-map-pin-line text-xs" />
                  </span>
                  <div className="min-w-0">
                    <div className="font-label text-[9px] text-[var(--lum-gold)]/85 mb-0.5">
                      PLACE
                    </div>
                    <div className="font-jp text-[12px] text-[var(--lum-cream)]/85 truncate">
                      {current.provenance}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Details */}
          <div key={`${current.id}-detail`} className="lg:col-span-5 flex flex-col justify-between gap-8 lum-quote-in">
            <div>
              {current.french && (
                <div className="font-label text-[11px] text-[var(--lum-gold)] mb-4">
                  / {current.french.toUpperCase()}
                </div>
              )}
              <h5
                key={`name-${current.id}`}
                className="font-jp text-3xl md:text-4xl lg:text-[48px] leading-[1.25] text-[var(--lum-cream)] mb-5 font-medium"
              >
                {renderLetters(current.name)}
              </h5>
              <p className="font-jp text-xl text-[var(--lum-gold)]/90 mb-7">
                {current.tagline}
              </p>
              <p className="font-jp text-base md:text-lg leading-[2.0] text-[var(--lum-cream)]/70">
                {current.description}
              </p>
            </div>

            {/* Quote + Ritual */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-[var(--lum-gold)]/12 pt-6">
              <div className="group p-5 rounded-md bg-[#e6f0ff] border border-[var(--lum-gold)]/15 transition-all duration-500 hover:border-[var(--lum-gold)]/40 hover:-translate-y-0.5">
                <div className="flex items-center gap-2 mb-3">
                  <i className="ri-chat-quote-line text-[var(--lum-gold)] text-sm transition-transform duration-500 group-hover:rotate-12" />
                  <span className="font-label text-[10px] text-[var(--lum-gold)]/90">
                    WORDS
                  </span>
                </div>
                <p className="font-jp text-[13px] leading-[1.8] text-[var(--lum-cream)]/75 italic">
                  {current.lightStudy}
                </p>
              </div>
              <div className="group p-5 rounded-md bg-[#e6f0ff] border border-[var(--lum-gold)]/15 transition-all duration-500 hover:border-[var(--lum-gold)]/40 hover:-translate-y-0.5">
                <div className="flex items-center gap-2 mb-3">
                  <i className="ri-heart-line text-[var(--lum-gold)] text-sm transition-transform duration-500 group-hover:scale-125" />
                  <span className="font-label text-[10px] text-[var(--lum-gold)]/90">
                    PROMISE
                  </span>
                </div>
                <p className="font-jp text-[13px] leading-[1.8] text-[var(--lum-cream)]/75 italic">
                  {current.ritual}
                </p>
              </div>
            </div>

            <ul className="space-y-3">
              {current.features.map((f, i) => (
                <li
                  key={f}
                  className="flex items-center gap-3 font-jp text-sm text-[var(--lum-cream)]/80 opacity-0 lum-quote-in"
                  style={{ animationDelay: `${200 + i * 120}ms` }}
                >
                  <span className="w-5 h-5 flex items-center justify-center text-[var(--lum-gold)]">
                    <i className="ri-check-line text-base"></i>
                  </span>
                  {f}
                </li>
              ))}
            </ul>

            <div className="flex items-center gap-4">
              <a
                href="#booking"
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById("booking");
                  if (el) {
                    const top = el.getBoundingClientRect().top + window.scrollY - 80;
                    window.scrollTo({ top, behavior: "smooth" });
                  }
                }}
                className="group inline-flex items-center gap-3 px-6 py-3.5 bg-[var(--lum-gold)] text-white font-jp text-[13px] rounded-full hover:bg-[#1d4ed8] transition-all duration-400 cursor-pointer whitespace-nowrap hover:-translate-y-0.5"
              >
                体験入部する
                <i className="ri-arrow-right-line text-base transition-transform duration-400 group-hover:translate-x-1" />
              </a>
            </div>
          </div>
        </div>

        {/* Beyond the curtain strip — club info */}
        <div className="mt-24 md:mt-32 pt-16 md:pt-20 border-t border-[var(--lum-gold)]/12">
          <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
            <div className="max-w-2xl">
              <div className="font-label text-[11px] text-[var(--lum-gold)] mb-5">
                / この会について
              </div>
              <h4 className="font-jp text-4xl md:text-5xl lg:text-[56px] leading-[1.3] text-[var(--lum-cream)] font-medium">
                <a href="#collections-grid" className="cursor-pointer lum-underline">
                  この会に来る人は、<span className="text-[var(--lum-gold)]">こんな人です。</span>
                </a>
              </h4>
            </div>
            <p className="font-jp text-sm md:text-[15px] text-[var(--lum-cream)]/60 max-w-sm leading-[2.0]">
              参加者、場所、人数、部費について。気になることがあれば、まずここを読んでみてください。
            </p>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--lum-gold)]/10 border border-[var(--lum-gold)]/10 rounded-lg overflow-hidden">
            {beyondCurtain.map((b, i) => (
              <Reveal
                key={b.label}
                delay={Math.min(5, i + 1) as 1 | 2 | 3 | 4 | 5}
                className="group relative bg-[var(--lum-bg)] p-7 md:p-8 flex flex-col gap-5 hover:bg-[#e6f0ff] transition-colors duration-500 overflow-hidden"
              >
                <span className="absolute inset-0 bg-gradient-to-br from-[var(--lum-gold)]/0 via-[var(--lum-gold)]/0 to-[var(--lum-gold)]/0 group-hover:from-[var(--lum-gold)]/5 group-hover:to-[var(--lum-gold)]/0 transition-all duration-700 pointer-events-none" />
                <div className="flex items-start justify-between gap-3 relative">
                  <div className="w-10 h-10 flex items-center justify-center rounded-md bg-[var(--lum-gold)]/10 text-[var(--lum-gold)] border border-[var(--lum-gold)]/25 transition-all duration-500 group-hover:scale-110 group-hover:bg-[var(--lum-gold)]/20">
                    <i className={`${b.icon} text-base`} />
                  </div>
                  <span className="font-label text-[9px] text-[var(--lum-cream)]/40 mt-2 transition-colors duration-500 group-hover:text-[var(--lum-cream)]/70">
                    {b.label}
                  </span>
                </div>
                <div className="relative">
                  <h6 className="font-jp text-base md:text-[17px] leading-[1.4] text-[var(--lum-cream)] mb-3 transition-transform duration-500 group-hover:translate-x-0.5 lum-sweep-line inline-block font-medium">
                    {b.title}
                  </h6>
                  <p className="font-jp text-[13px] leading-[1.9] text-[var(--lum-cream)]/60">
                    {b.description}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}